import { createAsyncThunk } from "@reduxjs/toolkit";
import { phpClient } from "../../../network";
import {
    AttendanceTags,
    DepartmentMaster,
    GetUnreadNotificationCountRequest as GetUnreadNotifCountRequest,
    IRejectWithValue,
    JobCode,
    PhpResponse,
    SiteType,
    TimeoffCategoryType,
    WorkflowRequest,
} from "../../../types";
import { CommonState } from "../../../types/state/common";
import { LastPunchData } from "../../../types/state/lastpunch";
import { isCacheValid, getCachedData, setCachedData } from "../../../utils/storageCache";

// Cache keys
const CACHE_KEYS = {
    SITES: 'cached_sites',
    DEPARTMENTS: 'cached_departments',
    JOB_CODES: 'cached_job_codes',
    ATTENDANCE_TAGS: 'cached_attendance_tags'
};

// Define state shape for thunk
type ThunkState = { common: CommonState };

export const getSites = createAsyncThunk<PhpResponse<SiteType[]>, void, { state: ThunkState }>(
    "/sites", 
    async (_, { rejectWithValue, getState }) => {
    try {
        const state = getState();
        // Check if we have valid cached data
        const timestamp = state.common.sitesTimestamp;
        if (isCacheValid(timestamp)) {
            const cachedData = getCachedData<PhpResponse<SiteType[]>>(CACHE_KEYS.SITES);
            if (cachedData) {
                return cachedData.data;
            }
        }

        const response = await phpClient.get<PhpResponse<SiteType[]>>({
            url: "/webclock/locations_list",
        });
        
        if (response) {
            // Cache the response
            setCachedData(CACHE_KEYS.SITES, response);
            return response;
        }

        return { data: [] } as PhpResponse<SiteType[]>;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getDepartments = createAsyncThunk<PhpResponse<DepartmentMaster[]>, void, {state: ThunkState}>(
    "/departments", 
    async (_, { rejectWithValue, getState }) => {
    const state = getState();

    try {
        // Check if we have valid cached data
        const timestamp = state.common.departmentsTimestamp;
        if (isCacheValid(timestamp)) {
            const cachedData = getCachedData<PhpResponse<DepartmentMaster[]>>(CACHE_KEYS.DEPARTMENTS);
            if (cachedData) {
                return cachedData.data;
            }
        }

        const response = await phpClient.get<PhpResponse<DepartmentMaster[]>>({
            url: "/webclock/department_list",
        });
        
        // Cache the response
        setCachedData(CACHE_KEYS.DEPARTMENTS, response);
        
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});
export const getJobCodes = createAsyncThunk("/Jobcodes", async (undefined, thunkAPI) => {
    const { rejectWithValue, getState } = thunkAPI;
    const state = getState() as ThunkState;

    try {
        // Check if we have valid cached data
        const timestamp = state.common.jobCodesTimestamp;
        if (isCacheValid(timestamp)) {
            const cachedData = getCachedData<PhpResponse<JobCode[]>>(CACHE_KEYS.JOB_CODES);
            if (cachedData) {
                return cachedData.data;
            }
        }

        const response = await phpClient.get<PhpResponse<JobCode[]>>({
            url: "/webclock/work_codes_list",
        });
        
        // Cache the response
        setCachedData(CACHE_KEYS.JOB_CODES, response);
        
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const fetchAttendanceTags = createAsyncThunk<PhpResponse<AttendanceTags[]>, void, IRejectWithValue>(
  "attendance/fetchAttendanceTags",
  async (_, { rejectWithValue }) => {
    try {
      const response = await phpClient.post<PhpResponse<AttendanceTags[]>>({
        url: "/attendance/attendance_tags",
      });
      return response || [];
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred while fetching attendance tags.");
    }
  }
);

export const getAllTimeoffCategories = createAsyncThunk<TimeoffCategoryType[], void, IRejectWithValue>(
    "timeoff/getTimeoffCategoryList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<PhpResponse<TimeoffCategoryType[]>>({
                url: "/timeoff/timeoff_category_list",
            });

            return response.data || [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getAssignedTimeoffCategories = createAsyncThunk<TimeoffCategoryType[], void, IRejectWithValue>(
    "timeoff/getAssginedTimeoffCategoryList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<TimeoffCategoryType[]>>({
                url: "/timeoff/list_assigned_category",
            });

            return response.data || [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);



export const callPhpWorkflow = createAsyncThunk<{ header_id: string } | undefined, WorkflowRequest, IRejectWithValue>(
    "php/workflow",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<{ header_id: string }>>({
                // url: "/workflow/create",
                url: "/workflow3/process_step",
                data,
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getUnreadNotifCount = createAsyncThunk<number | undefined, GetUnreadNotifCountRequest, IRejectWithValue>(
    "common/getUnreadMessageCount",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<number>>({
                url: "/notification/notification_unread_count",
                data
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getLastPunch = createAsyncThunk("common/lastpunch", async (undefined, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;

    try {
        const response = await phpClient.get<PhpResponse<SiteType[]>>({
            url: "webclock/lastpunch",
        });
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});


// Async thunk to fetch the last punch
export const fetchLastPunch = createAsyncThunk<
LastPunchData,
  string, 
  { rejectValue: string }
>(
  "lastPunch/fetchLastPunch",
  async (datetime: string, { rejectWithValue }) => {
    try {
      const response = await phpClient.get<PhpResponse<LastPunchData>>({
        url: `/webclock/lastpunch?datetime=${datetime}`,
      });
      if (response.data) {
        return response.data;
      } else {
        throw new Error("Invalid response data");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);